<script setup lang="ts">
import { computed, ref } from 'vue';
import type { DosageForm } from '@/api/medications';
import { useCompanyClock } from '@/composables/useCompanyClock';
import {
  INTERVALS, WEEKDAYS, detectInterval, doseStep, everyHours, fromFirst, humanMoment, minutesToTime, nextDose,
  parseTime,
} from '@/utils/medications';

// One dosage as a card: how much, how often and the first dose; the clock
// times it gives are listed below. A frequency fills the times from the
// first dose; "Editar horas" turns them into times set by hand. Instructions
// and weekdays, rarely needed, wait behind one link. With several dosages
// the card is titled and can be removed.
const props = defineProps<{
  title: string; doseUnit: string; doseUnitLabel: string; start: Date | null; showErrors: boolean;
}>();
const emit = defineEmits<{ remove: [] }>();
const dosage = defineModel<DosageForm>({ required: true });
const { now } = useCompanyClock();

const MORNING = 480;
const INSTRUCTION_PRESETS = ['Con comida', 'En ayunas', 'Triturar'];
type Frequency = number | 'custom' | 'as_needed' | 'single';

const detected = detectInterval(dosage.value.timesOfDay);
const firstTime = ref(detected?.first ?? dosage.value.timesOfDay[0] ?? MORNING);
const firstText = ref(minutesToTime(firstTime.value));
const custom = ref(dosage.value.timesOfDay.length > 0 && !detected);
const more = ref(!!dosage.value.instructions || !dosage.value.everyDay);

const frequency = computed<Frequency | null>(() => {
  if (dosage.value.kind !== 'scheduled') return dosage.value.kind;
  if (custom.value) return 'custom';

  return detectInterval(dosage.value.timesOfDay)?.hours ?? null;
});

const frequencies = [
  ...INTERVALS.map(({ hours, title }) => ({ value: hours as Frequency, title })),
  { value: 'custom' as Frequency, title: 'Horas específicas' },
  { value: 'as_needed' as Frequency, title: 'SOS (si es necesario)' },
  { value: 'single' as Frequency, title: 'Dosis única' },
];

function chooseFrequency(value: Frequency | null) {
  if (value === null) return;
  if (value === 'as_needed' || value === 'single') {
    dosage.value.kind = value;

    return;
  }
  dosage.value.kind = 'scheduled';
  custom.value = value === 'custom';
  if (typeof value === 'number') dosage.value.timesOfDay = everyHours(firstTime.value, value);
}

const firstError = ref('');
const firstLabel = computed(() => (frequency.value === INTERVALS[0].hours ? 'Hora' : 'Primera dosis'));

function changeFirstTime() {
  const minutes = parseTime(firstText.value);
  if (minutes === null) {
    firstError.value = 'Hora no válida';

    return;
  }
  firstError.value = '';
  firstTime.value = minutes;
  firstText.value = minutesToTime(minutes);
  if (typeof frequency.value === 'number') dosage.value.timesOfDay = everyHours(minutes, frequency.value);
}

// The times as given from the first dose, and when the first one comes.
const sequence = computed(() => fromFirst(dosage.value.timesOfDay, firstTime.value).map(minutesToTime).join(' → '));
const firstDose = computed(() => {
  const from = props.start && props.start > now() ? props.start : now();
  const dose = nextDose([dosage.value], from);

  return dose ? humanMoment(dose, now()) : null;
});

const editing = ref<number | 'new' | null>(null);
const editedTime = ref('');

function openTime(minutes: number | 'new') {
  editing.value = minutes;
  editedTime.value = minutes === 'new' ? '' : minutesToTime(minutes);
}

function saveTime() {
  const minutes = parseTime(editedTime.value);
  if (minutes === null) return;
  const others = dosage.value.timesOfDay.filter((time) => time !== editing.value);
  dosage.value.timesOfDay = [...new Set([...others, minutes])].sort((a, b) => a - b);
  editing.value = null;
}

function removeTime(minutes: number) {
  dosage.value.timesOfDay = dosage.value.timesOfDay.filter((time) => time !== minutes);
}

const step = computed(() => doseStep(props.doseUnit));

function stepQuantity(direction: number) {
  const next = (dosage.value.doseQuantity ?? 0) + direction * step.value;
  dosage.value.doseQuantity = Math.max(step.value, next);
}

function instructionParts(): string[] {
  return dosage.value.instructions.split(',').map((part) => part.trim()).filter(Boolean);
}

function sameInstruction(part: string, preset: string): boolean {
  return part.toLowerCase() === preset.toLowerCase();
}

function toggleInstruction(preset: string) {
  const parts = instructionParts();
  const next = parts.some((part) => sameInstruction(part, preset)) ?
    parts.filter((part) => !sameInstruction(part, preset)) :
    [...parts, preset];
  dosage.value.instructions = next.join(', ');
}

const activeInstructions = computed(() => INSTRUCTION_PRESETS.filter((preset) => (
  instructionParts().some((part) => sameInstruction(part, preset))
)));

const someDays = computed({
  get: () => !dosage.value.everyDay,
  set: (value: boolean) => {
    dosage.value.everyDay = !value;
  },
});

const scheduled = computed(() => dosage.value.kind === 'scheduled');
const moreLabel = computed(() => (scheduled.value ? 'Instrucciones o días específicos' : 'Instrucciones'));

const quantityError = computed(() => (
  props.showErrors && !(dosage.value.doseQuantity && dosage.value.doseQuantity > 0) ? 'Indica la cantidad' : ''
));
const frequencyError = computed(() => (props.showErrors && frequency.value === null ? 'Elige cada cuánto' : ''));
const timesError = computed(() => (
  props.showErrors && frequency.value === 'custom' && dosage.value.timesOfDay.length === 0 ?
    'Agrega al menos una hora' :
    ''
));
const indicationError = computed(() => (
  props.showErrors && dosage.value.kind === 'as_needed' && !dosage.value.indication.trim() ?
    'Indica cuándo darla' :
    ''
));
const daysError = computed(() => (
  props.showErrors && scheduled.value && !dosage.value.everyDay && dosage.value.weekdays.length === 0 ?
    'Elige los días' :
    ''
));
</script>
<template>
  <v-sheet
    border
    rounded
    class="pa-3 d-flex flex-column ga-3"
  >
    <div
      v-if="title"
      class="d-flex align-center"
    >
      <span class="text-subtitle-2">{{ title }}</span>
      <v-spacer />
      <v-btn
        variant="text"
        size="small"
        color="error"
        prepend-icon="mdi-delete-outline"
        :aria-label="`Quitar ${title.toLowerCase()}`"
        @click="emit('remove')"
      >
        Quitar
      </v-btn>
    </div>
    <div class="d-flex flex-wrap align-start ga-3">
      <v-text-field
        v-model.number="dosage.doseQuantity"
        type="number"
        min="0"
        step="0.25"
        :label="doseUnitLabel ? `Cantidad (${doseUnitLabel})` : 'Cantidad'"
        variant="outlined"
        density="compact"
        class="quantity-field"
        :error-messages="quantityError"
        :hide-details="!quantityError"
        aria-label="Cantidad por dosis"
      >
        <template #prepend-inner>
          <v-btn
            icon="mdi-minus"
            size="x-small"
            variant="text"
            aria-label="Menos"
            @click="stepQuantity(-1)"
          />
        </template>
        <template #append-inner>
          <v-btn
            icon="mdi-plus"
            size="x-small"
            variant="text"
            aria-label="Más"
            @click="stepQuantity(1)"
          />
        </template>
      </v-text-field>
      <v-select
        :model-value="frequency"
        :items="frequencies"
        label="Frecuencia"
        placeholder="¿Cada cuánto?"
        persistent-placeholder
        aria-label="Frecuencia"
        variant="outlined"
        density="compact"
        class="frequency-field"
        :error-messages="frequencyError"
        :hide-details="!frequencyError"
        @update:model-value="chooseFrequency"
      />
      <template v-if="typeof frequency === 'number'">
        <v-text-field
          v-model="firstText"
          :label="firstLabel"
          inputmode="numeric"
          placeholder="08:00"
          aria-label="Hora de la primera dosis"
          variant="outlined"
          density="compact"
          class="time-field"
          :error-messages="firstError"
          :hide-details="!firstError"
          @blur="changeFirstTime"
          @keyup.enter="changeFirstTime"
        />
      </template>
    </div>

    <div
      v-if="typeof frequency === 'number'"
      class="d-flex flex-wrap align-center ga-2 text-body-2 text-medium-emphasis"
    >
      <v-icon
        icon="mdi-clock-outline"
        size="small"
      />
      <span>{{ sequence }}<template v-if="firstDose"> · primera: {{ firstDose }}</template></span>
      <v-btn
        variant="text"
        size="small"
        density="comfortable"
        class="px-1"
        @click="custom = true"
      >
        Editar horas
      </v-btn>
    </div>

    <div v-else-if="frequency === 'custom'">
      <div class="d-flex flex-wrap align-center ga-2">
        <v-menu
          v-for="time in dosage.timesOfDay"
          :key="time"
          :model-value="editing === time"
          :close-on-content-click="false"
          @update:model-value="(open) => open ? openTime(time) : (editing = null)"
        >
          <template #activator="{ props: menuProps }">
            <v-chip
              v-bind="menuProps"
              closable
              prepend-icon="mdi-clock-outline"
              @click:close="removeTime(time)"
            >
              {{ minutesToTime(time) }}
            </v-chip>
          </template>
          <v-card class="pa-3 d-flex align-center ga-2">
            <v-text-field
              v-model="editedTime"
              inputmode="numeric"
              placeholder="08:00"
              aria-label="Hora"
              variant="outlined"
              density="compact"
              hide-details
              autofocus
              class="time-field"
              @keyup.enter="saveTime"
            />
            <v-btn
              color="primary"
              variant="flat"
              size="small"
              @click="saveTime"
            >
              Listo
            </v-btn>
          </v-card>
        </v-menu>
        <v-menu
          :model-value="editing === 'new'"
          :close-on-content-click="false"
          @update:model-value="(open) => open ? openTime('new') : (editing = null)"
        >
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="text"
              size="small"
              prepend-icon="mdi-plus"
            >
              Agregar hora
            </v-btn>
          </template>
          <v-card class="pa-3 d-flex align-center ga-2">
            <v-text-field
              v-model="editedTime"
              inputmode="numeric"
              placeholder="08:00"
              aria-label="Hora"
              variant="outlined"
              density="compact"
              hide-details
              autofocus
              class="time-field"
              @keyup.enter="saveTime"
            />
            <v-btn
              color="primary"
              variant="flat"
              size="small"
              :disabled="parseTime(editedTime) === null"
              @click="saveTime"
            >
              Agregar
            </v-btn>
          </v-card>
        </v-menu>
      </div>
      <div
        v-if="timesError"
        class="text-caption text-error mt-1"
      >
        {{ timesError }}
      </div>
    </div>

    <div
      v-else-if="dosage.kind === 'as_needed'"
      class="d-flex flex-wrap align-start ga-2"
    >
      <v-text-field
        v-model="dosage.indication"
        label="Cuándo darla"
        placeholder="Ej.: si dolor mayor a 5"
        variant="outlined"
        density="compact"
        class="indication-field"
        :error-messages="indicationError"
        :hide-details="!indicationError"
      />
      <v-text-field
        v-model.number="dosage.maxDosesPer24h"
        type="number"
        min="1"
        prefix="máx."
        suffix="en 24 h"
        aria-label="Máximo en 24 horas"
        variant="outlined"
        density="compact"
        hide-details
        class="max-field"
      />
    </div>

    <div
      v-else-if="dosage.kind === 'single'"
      class="text-body-2 text-medium-emphasis"
    >
      Se da una vez, al empezar la indicación.
    </div>

    <div v-if="frequency !== null">
      <v-btn
        v-if="!more"
        variant="text"
        size="small"
        prepend-icon="mdi-plus"
        class="px-1"
        @click="more = true"
      >
        {{ moreLabel }}
      </v-btn>
      <div
        v-else
        class="d-flex flex-column ga-2"
      >
        <div class="d-flex flex-wrap align-center ga-2">
          <v-chip
            v-for="preset in INSTRUCTION_PRESETS"
            :key="preset"
            size="small"
            :color="activeInstructions.includes(preset) ? 'primary' : undefined"
            :variant="activeInstructions.includes(preset) ? 'tonal' : 'outlined'"
            @click="toggleInstruction(preset)"
          >
            {{ preset }}
          </v-chip>
          <v-text-field
            v-model="dosage.instructions"
            placeholder="Otra instrucción para quien la da"
            aria-label="Instrucciones"
            variant="outlined"
            density="compact"
            hide-details
            class="instructions-field"
          />
        </div>
        <div
          v-if="scheduled"
          class="d-flex flex-wrap align-center ga-2"
        >
          <v-checkbox
            v-model="someDays"
            label="Solo algunos días"
            density="compact"
            hide-details
            class="flex-grow-0"
          />
          <v-chip-group
            v-if="someDays"
            v-model="dosage.weekdays"
            multiple
            selected-class="text-primary"
          >
            <v-chip
              v-for="day in WEEKDAYS"
              :key="day.value"
              :value="day.value"
              size="small"
              filter
              variant="outlined"
            >
              {{ day.title }}
            </v-chip>
          </v-chip-group>
        </div>
        <div
          v-if="daysError"
          class="text-caption text-error"
        >
          {{ daysError }}
        </div>
      </div>
    </div>
  </v-sheet>
</template>
<style scoped>
.quantity-field {
  flex: 0 0 180px;
}

.frequency-field {
  flex: 0 0 200px;
}

.time-field {
  flex: 0 0 136px;
  max-width: 136px;
}

.indication-field {
  flex: 1 1 240px;
}

.max-field {
  flex: 0 0 170px;
}

.instructions-field {
  flex: 1 1 220px;
}
</style>
